import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListScoreRules,
  useCreateScoreRule,
  useUpdateScoreRule,
  useDeleteScoreRule,
  useRecalculateScores,
  getListScoreRulesQueryKey,
  getListLeadsQueryKey,
  getGetLeadsSummaryQueryKey,
  type ScoreRule,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash, Save, RefreshCw } from "lucide-react";

const FIELDS = ["urgency", "stage", "source", "spaceType", "numberVerified", "callStatus", "leadStatus"] as const;

const FIELD_VALUES: Record<string, string[]> = {
  urgency: ["low", "medium", "high", "immediate"],
  stage: ["not_contacted", "contacted", "proposal_sent", "tour_scheduled", "tour_done", "follow_up", "closed_won", "closed_loss"],
  source: ["organic_website", "meta_ads", "google_ads", "ads_website", "blog", "walk_in", "referral", "call", "other"],
  spaceType: ["private_cabin", "day_pass", "five_days_pass", "seven_days_pass", "workstation", "meeting_room", "podcast_room", "conference_room"],
  numberVerified: ["true", "false"],
  callStatus: ["not_started", "not_reachable", "not_answered", "connected", "disconnected", "not_answered_1", "not_answered_2", "not_answered_3"],
  leadStatus: ["new", "qualified", "disqualified", "converted"],
};

interface RuleEditorProps {
  initial?: { field: string; value: string; points: number; description?: string | null };
  onSubmit: (data: { field: string; value: string; points: number; description: string | null }) => void;
  onCancel?: () => void;
  saving?: boolean;
  submitLabel: string;
}

function RuleEditor({ initial, onSubmit, onCancel, saving, submitLabel }: RuleEditorProps) {
  const [field, setField] = useState(initial?.field ?? "urgency");
  const [value, setValue] = useState(initial?.value ?? FIELD_VALUES.urgency![0]!);
  const [points, setPoints] = useState(initial?.points ?? 10);
  const [description, setDescription] = useState(initial?.description ?? "");
  return (
    <div className="grid gap-3 md:grid-cols-5 items-end">
      <div>
        <Label className="text-xs">Field</Label>
        <Select value={field} onValueChange={(v) => { setField(v); setValue(FIELD_VALUES[v]?.[0] ?? ""); }}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FIELDS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Value</Label>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(FIELD_VALUES[field] ?? []).map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Points</Label>
        <Input type="number" value={points} onChange={(e) => setPoints(Number(e.target.value))} className="h-9" />
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs">Description</Label>
        <Input value={description ?? ""} onChange={(e) => setDescription(e.target.value)} className="h-9" />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSubmit({ field, value, points, description: description || null })} disabled={saving} size="sm"><Save className="mr-1 h-4 w-4" />{submitLabel}</Button>
        {onCancel && <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}

export default function ScoreBuilder() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: rules, isLoading } = useListScoreRules();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getListScoreRulesQueryKey() });
  };

  const createMutation = useCreateScoreRule({
    mutation: {
      onSuccess: () => { toast({ title: "Rule created" }); invalidate(); setShowAdd(false); },
      onError: () => toast({ title: "Failed to create rule", variant: "destructive" }),
    },
  });
  const updateMutation = useUpdateScoreRule({
    mutation: {
      onSuccess: () => { toast({ title: "Rule updated" }); invalidate(); setEditingId(null); },
      onError: () => toast({ title: "Failed to update rule", variant: "destructive" }),
    },
  });
  const deleteMutation = useDeleteScoreRule({
    mutation: {
      onSuccess: () => { toast({ title: "Rule deleted" }); invalidate(); },
      onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
    },
  });
  const recalcMutation = useRecalculateScores({
    mutation: {
      onSuccess: (r) => {
        toast({ title: `Recalculated ${r.updated} leads` });
        void queryClient.invalidateQueries({ queryKey: getListLeadsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
      },
      onError: () => toast({ title: "Recalculate failed", variant: "destructive" }),
    },
  });

  if (!isSuperAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold tracking-tight">Lead Score Builder</h1>
        <Card><CardContent className="p-6 text-muted-foreground">Only super admins can configure scoring rules. Current rules:</CardContent></Card>
        <Card>
          <CardContent className="p-0">
            <RulesTable rules={rules ?? []} loading={isLoading} readOnly />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lead Score Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure how each lead attribute contributes to its priority score (0–100). Base score is 30. Closed-won = 100, closed-loss = 0.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => recalcMutation.mutate()} disabled={recalcMutation.isPending}>
            <RefreshCw className={`mr-2 h-4 w-4 ${recalcMutation.isPending ? "animate-spin" : ""}`} />
            Recalculate All Leads
          </Button>
          <Button onClick={() => setShowAdd(true)} disabled={showAdd}><Plus className="mr-2 h-4 w-4" />Add Rule</Button>
        </div>
      </div>

      {showAdd && (
        <Card>
          <CardHeader><CardTitle className="text-base">New Rule</CardTitle></CardHeader>
          <CardContent>
            <RuleEditor
              submitLabel="Create"
              saving={createMutation.isPending}
              onCancel={() => setShowAdd(false)}
              onSubmit={(data) => createMutation.mutate({ data })}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <RulesTable
            rules={rules ?? []}
            loading={isLoading}
            editingId={editingId}
            onEdit={setEditingId}
            onCancelEdit={() => setEditingId(null)}
            onUpdate={(id, data) => updateMutation.mutate({ id, data })}
            onDelete={(id) => { if (confirm("Delete this rule?")) deleteMutation.mutate({ id }); }}
            updating={updateMutation.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
}

interface RulesTableProps {
  rules: ScoreRule[];
  loading: boolean;
  editingId?: number | null;
  onEdit?: (id: number) => void;
  onCancelEdit?: () => void;
  onUpdate?: (id: number, data: { field: string; value: string; points: number; description: string | null }) => void;
  onDelete?: (id: number) => void;
  updating?: boolean;
  readOnly?: boolean;
}

function RulesTable({ rules, loading, editingId, onEdit, onCancelEdit, onUpdate, onDelete, updating, readOnly }: RulesTableProps) {
  if (loading) return <div className="p-4"><Skeleton className="h-32 w-full" /></div>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Field</TableHead>
          <TableHead>Value</TableHead>
          <TableHead className="w-24">Points</TableHead>
          <TableHead>Description</TableHead>
          {!readOnly && <TableHead className="w-48">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rules.length === 0 && (
          <TableRow><TableCell colSpan={readOnly ? 4 : 5} className="text-center text-muted-foreground py-6">No rules yet.</TableCell></TableRow>
        )}
        {rules.map((rule) => editingId === rule.id ? (
          <TableRow key={rule.id}>
            <TableCell colSpan={readOnly ? 4 : 5}>
              <RuleEditor
                initial={rule}
                submitLabel="Save"
                saving={updating}
                onCancel={onCancelEdit}
                onSubmit={(data) => onUpdate?.(rule.id, data)}
              />
            </TableCell>
          </TableRow>
        ) : (
          <TableRow key={rule.id}>
            <TableCell className="font-mono text-sm">{rule.field}</TableCell>
            <TableCell className="capitalize">{rule.value.replace(/_/g, " ")}</TableCell>
            <TableCell className={rule.points >= 0 ? "text-green-700" : "text-red-700"}>{rule.points >= 0 ? "+" : ""}{rule.points}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{rule.description}</TableCell>
            {!readOnly && (
              <TableCell>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onEdit?.(rule.id)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete?.(rule.id)}><Trash className="h-4 w-4" /></Button>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
